import { shippingTextSchema } from "@/lib/shipping-text";
import { useState, useEffect } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  type CreateContactInput,
  type CustomerContact,
  PropertyType,
} from "../types";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  SelectItem,
} from "@heroui/react";
import {
  HeroFormField,
  HeroInputField,
  HeroSelectField,
  HeroSwitchField,
  HeroTextareaField,
} from "@/components/hero-form";
import { PhoneInputShadcn } from "@/components/ui/phone-input-shadcn";
import {
  Select as UiSelect,
  SelectContent as UiSelectContent,
  SelectItem as UiSelectItem,
  SelectTrigger as UiSelectTrigger,
  SelectValue as UiSelectValue,
} from "@/components/ui/select";
import { useParseContact } from "../api";
import { Sparkles, MapPin } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  geocodeAddress,
  type AddressComponents,
} from "@/services/google-geocoding";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { countryCodeForValue, getCountryOptions } from "@/lib/countries";

const createContactFormSchema = (t: TFunction) =>
  z.object({
    contactName: shippingTextSchema(t("validation.shippingText"))
      .min(1, t("validation.nameRequired"))
      .max(100),
    contactCompany: shippingTextSchema(t("validation.shippingText"))
      .max(100)
      .optional(),
    contactPhone: z.string().min(1, t("validation.phoneRequired")).max(50),
    contactEmail: z
      .string()
      .email(t("validation.emailInvalid"))
      .or(z.literal(""))
      .optional(),
    addressLine1: shippingTextSchema(t("validation.shippingText"))
      .min(1, t("validation.addressLine1Required"))
      .max(200),
    addressLine2: shippingTextSchema(t("validation.shippingText"))
      .max(200)
      .optional(),
    addressCity: shippingTextSchema(t("validation.shippingText"))
      .min(1, t("validation.cityRequired"))
      .max(100),
    addressProvince: z
      .string()
      .min(1, t("validation.provinceRequired"))
      .max(100),
    addressCountry: z.string().length(2, t("validation.countryRequired")),
    addressPostalCode: z
      .string()
      .min(1, t("validation.postalCodeRequired"))
      .max(20),
    label: z.string().max(50).optional(),
    pickupInstructions: z.string().max(500).optional(),
    deliveryInstructions: z.string().max(500).optional(),
    isDefault: z.boolean(),
    isFavorite: z.boolean(),
    propertyResidential: z.boolean(),
    propertyType: z
      .union([z.nativeEnum(PropertyType), z.literal("")])
      .optional(),
  });

type ContactFormValues = z.infer<ReturnType<typeof createContactFormSchema>>;

interface ContactFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateContactInput) => void;
  initialData?: CustomerContact;
  isLoading?: boolean;
}

export function ContactForm({
  open,
  onClose,
  onSubmit,
  initialData,
  isLoading = false,
}: ContactFormProps) {
  const { t, i18n } = useTranslation("app");
  const countryOptions = getCountryOptions(i18n.language);
  const contactFormSchema = createContactFormSchema(t);
  const [smartInput, setSmartInput] = useState("");
  const [showUnverifiedConfirm, setShowUnverifiedConfirm] = useState(false);
  const [showSuggestionDialog, setShowSuggestionDialog] = useState(false);
  const [pendingValues, setPendingValues] = useState<ContactFormValues | null>(
    null
  );
  const [suggestedAddress, setSuggestedAddress] =
    useState<AddressComponents | null>(null);
  const [verifiedLocation, setVerifiedLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [isGeocoding, setIsGeocoding] = useState(false);
  const { toast } = useToast();
  const parseContactMutation = useParseContact();

  const getDefaultValues = (): ContactFormValues => ({
    contactName: "",
    contactCompany: "",
    contactPhone: "",
    contactEmail: "",
    addressLine1: "",
    addressLine2: "",
    addressCity: "",
    addressProvince: "",
    addressCountry: "CA",
    addressPostalCode: "",
    label: "",
    pickupInstructions: "",
    deliveryInstructions: "",
    isDefault: false,
    isFavorite: false,
    propertyResidential: true,
    propertyType: "",
  });

  const mapInitialDataToFormValues = (
    data: CustomerContact
  ): ContactFormValues => ({
    contactName: data.contactName,
    contactCompany: data.contactCompany ?? "",
    contactPhone: data.contactPhone ?? "",
    contactEmail: data.contactEmail ?? "",
    addressLine1: data.addressLine1,
    addressLine2: data.addressLine2 ?? "",
    addressCity: data.addressCity,
    addressProvince: data.addressProvince,
    addressCountry: countryCodeForValue(data.addressCountry) ?? "",
    addressPostalCode: data.addressPostalCode ?? "",
    label: data.label ?? "",
    pickupInstructions: data.pickupInstructions ?? "",
    deliveryInstructions: data.deliveryInstructions ?? "",
    isDefault: data.isDefault,
    isFavorite: data.isFavorite,
    propertyResidential: data.propertyResidential,
    propertyType: data.propertyType ?? "",
  });

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: initialData
      ? mapInitialDataToFormValues(initialData)
      : getDefaultValues(),
  });

  useEffect(() => {
    if (open) {
      form.reset(
        initialData
          ? mapInitialDataToFormValues(initialData)
          : getDefaultValues()
      );
      setSmartInput("");
      if (initialData?.location) {
        setVerifiedLocation(initialData.location);
      } else {
        setVerifiedLocation(null);
      }
    }
  }, [open, initialData, form]);

  useEffect(() => {
    const subscription = form.watch((_value, { name }) => {
      if (
        name &&
        [
          "addressLine1",
          "addressLine2",
          "addressCity",
          "addressProvince",
          "addressPostalCode",
          "addressCountry",
        ].includes(name)
      ) {
        setVerifiedLocation(null);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const handleSmartParse = async () => {
    if (!smartInput.trim()) {
      toast({
        title: t("addressBook.toast.noInputTitle"),
        description: t("addressBook.toast.noInputDescription"),
        variant: "destructive",
      });
      return;
    }

    try {
      const parsed = await parseContactMutation.mutateAsync(smartInput);

      if (parsed.name) form.setValue("contactName", parsed.name);
      if (parsed.company) form.setValue("contactCompany", parsed.company);
      if (parsed.phone) form.setValue("contactPhone", parsed.phone);
      if (parsed.email) form.setValue("contactEmail", parsed.email);
      if (parsed.address?.street1)
        form.setValue("addressLine1", parsed.address.street1);
      if (parsed.address?.street2)
        form.setValue("addressLine2", parsed.address.street2);
      if (parsed.address?.city)
        form.setValue("addressCity", parsed.address.city);
      if (parsed.address?.province)
        form.setValue("addressProvince", parsed.address.province);
      if (parsed.address?.postalCode)
        form.setValue("addressPostalCode", parsed.address.postalCode);
      if (parsed.address?.country) {
        form.setValue(
          "addressCountry",
          countryCodeForValue(parsed.address.country) ?? ""
        );
      }
      if (parsed.label) form.setValue("label", parsed.label);
      if (parsed.pickupInstructions)
        form.setValue("pickupInstructions", parsed.pickupInstructions);
      if (parsed.deliveryInstructions)
        form.setValue("deliveryInstructions", parsed.deliveryInstructions);

      if (parsed.propertyType === "Residential") {
        form.setValue("propertyResidential", true);
      } else if (parsed.propertyType === "Commercial") {
        form.setValue("propertyResidential", false);
      }

      setSmartInput("");

      toast({
        title: t("addressBook.toast.parsedTitle"),
        description: t("addressBook.toast.parsedDescription"),
      });
    } catch (error: unknown) {
      const message = axios.isAxiosError<{ message?: string }>(error)
        ? error.response?.data?.message
        : undefined;
      toast({
        title: t("addressBook.toast.parseFailedTitle"),
        description: message ?? t("addressBook.toast.parseFailedDescription"),
        variant: "destructive",
      });
    }
  };

  const submitWithLocation = (
    values: ContactFormValues,
    location: { lat: number; lng: number }
  ) => {
    const country = countryCodeForValue(values.addressCountry);
    if (!country) {
      form.setError("addressCountry", {
        message: t("validation.countryRequired"),
      });
      return;
    }
    const contactData: CreateContactInput = {
      ...values,
      addressCountry: country,
      propertyType:
        values.propertyType === "" ? undefined : values.propertyType,
      location,
    };
    onSubmit(contactData);
  };

  const addressDiffers = (
    userValues: ContactFormValues,
    suggested: AddressComponents
  ): boolean => {
    const normalize = (s: string | undefined) => (s ?? "").toLowerCase().trim();

    if (
      normalize(suggested.addressLine1) !== normalize(userValues.addressLine1)
    )
      return true;
    if (normalize(suggested.city) !== normalize(userValues.addressCity))
      return true;
    if (normalize(suggested.province) !== normalize(userValues.addressProvince))
      return true;
    if (
      suggested.postalCode &&
      normalize(suggested.postalCode) !==
        normalize(userValues.addressPostalCode)
    )
      return true;

    return false;
  };

  const handleUseSuggestedAddress = () => {
    if (suggestedAddress) {
      form.setValue("addressLine1", suggestedAddress.addressLine1);
      if (suggestedAddress.addressLine2) {
        form.setValue("addressLine2", suggestedAddress.addressLine2);
      }
      form.setValue("addressCity", suggestedAddress.city);
      form.setValue("addressProvince", suggestedAddress.province);
      form.setValue(
        "addressCountry",
        countryCodeForValue(suggestedAddress.country) ?? ""
      );
      if (suggestedAddress.postalCode) {
        form.setValue("addressPostalCode", suggestedAddress.postalCode);
      }

      toast({
        title: t("addressBook.toast.addressUpdatedTitle"),
        description: t("addressBook.toast.addressUpdatedDescription"),
      });
    }
    setShowSuggestionDialog(false);
    setSuggestedAddress(null);
    setPendingValues(null);
  };

  const handleKeepOriginalAddress = () => {
    setShowSuggestionDialog(false);
    setSuggestedAddress(null);
    setPendingValues(null);
  };

  const handleSubmit = async (values: ContactFormValues) => {
    if (verifiedLocation) {
      submitWithLocation(values, verifiedLocation);
      setVerifiedLocation(null);
      return;
    }

    setIsGeocoding(true);
    try {
      const result = await geocodeAddress({
        street1: values.addressLine1,
        street2: values.addressLine2,
        city: values.addressCity,
        state: values.addressProvince,
        postalCode: values.addressPostalCode ?? "",
        country: values.addressCountry,
      });

      if (!result) {
        setPendingValues(values);
        setConfirmMessage(t("addressBook.form.addressNotFoundBody"));
        setShowUnverifiedConfirm(true);
        return;
      }

      const location = {
        lat: result.latitude,
        lng: result.longitude,
      };

      if (result.isApproximate) {
        setPendingValues(values);
        setConfirmMessage(t("addressBook.form.approximateAddressBody"));
        setShowUnverifiedConfirm(true);
        return;
      }

      if (
        result.addressComponents &&
        addressDiffers(values, result.addressComponents)
      ) {
        setPendingValues(values);
        setSuggestedAddress(result.addressComponents);
        setVerifiedLocation(location);
        setShowSuggestionDialog(true);
        return;
      }

      submitWithLocation(values, location);
    } catch {
      setPendingValues(values);
      setConfirmMessage(t("addressBook.form.addressVerifyError"));
      setShowUnverifiedConfirm(true);
    } finally {
      setIsGeocoding(false);
    }
  };

  const isBusy = isLoading || isGeocoding;

  return (
    <>
      <Modal
        isOpen={open}
        onOpenChange={(o) => {
          if (!o) onClose();
        }}
        placement="center"
        backdrop="blur"
        disableAnimation
        size="2xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          {/* form must be a bounded flex column so HeroUI's inside-scroll
              ModalBody (flex-1 + overflow-y-auto) stays within the card —
              otherwise a plain form grows to content height and the body
              overflows past the modal background. */}
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex max-h-[inherit] w-full flex-col overflow-hidden"
          >
            <ModalHeader>
              {initialData
                ? t("addressBook.form.editTitle")
                : t("addressBook.form.addTitle")}
            </ModalHeader>
            <ModalBody className="gap-4">
              {!initialData && (
                <div className="space-y-3 p-4 bg-primary/5 rounded-lg border border-primary/20">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold text-foreground">
                      {t("addressBook.form.smartTitle")}
                    </h3>
                  </div>
                  <p className="text-sm text-default-600">
                    {t("addressBook.form.smartBody")}
                  </p>
                  <div className="space-y-2">
                    <textarea
                      value={smartInput}
                      onChange={(e) => setSmartInput(e.target.value)}
                      placeholder={t("addressBook.form.smartPlaceholder")}
                      rows={4}
                      className="w-full rounded-medium bg-background border border-default-200 p-3 text-small focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                    <p className="text-xs text-default-500">
                      <span className="font-medium">
                        {t("addressBook.form.exampleLabel")}
                      </span>{" "}
                      {t("addressBook.form.exampleText")}
                    </p>
                    <Button
                      type="button"
                      color="secondary"
                      variant="bordered"
                      fullWidth
                      isDisabled={
                        !smartInput.trim() || parseContactMutation.isPending
                      }
                      isLoading={parseContactMutation.isPending}
                      startContent={
                        !parseContactMutation.isPending ? (
                          <Sparkles className="h-4 w-4" />
                        ) : null
                      }
                      onPress={handleSmartParse}
                    >
                      {parseContactMutation.isPending
                        ? t("addressBook.form.parsing")
                        : t("addressBook.form.parseWithAi")}
                    </Button>
                  </div>
                </div>
              )}

              <p className="text-xs text-default-500">
                <span className="text-danger">*</span>{" "}
                {t("addressBook.form.requiredFields")}
              </p>

              <div className="space-y-4">
                <h3 className="font-semibold">
                  {t("addressBook.form.contactInfo")}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <HeroInputField
                    control={form.control}
                    name="contactName"
                    label={t("addressBook.form.name")}
                    placeholder={t("addressBook.form.namePlaceholder")}
                    variant="bordered"
                    labelPlacement="outside"
                  />
                  <HeroInputField
                    control={form.control}
                    name="contactCompany"
                    label={t("addressBook.form.company")}
                    placeholder={t("addressBook.form.companyPlaceholder")}
                    variant="bordered"
                    labelPlacement="outside"
                  />
                  <HeroFormField
                    control={form.control}
                    name="contactPhone"
                    render={({ field, fieldState }) => (
                      <div>
                        <label className="block text-tiny font-medium text-default-700 mb-1.5">
                          {t("addressBook.form.phone")}
                        </label>
                        <PhoneInputShadcn
                          value={field.value ?? ""}
                          onChange={(v) => field.onChange(v ?? "")}
                          onBlur={field.onBlur}
                          defaultCountry="CA"
                        />
                        {fieldState.error?.message && (
                          <p className="mt-1 text-tiny text-danger">
                            {fieldState.error.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                  <HeroInputField
                    control={form.control}
                    name="contactEmail"
                    label={t("addressBook.form.email")}
                    placeholder={t("addressBook.form.emailPlaceholder")}
                    type="email"
                    variant="bordered"
                    labelPlacement="outside"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold">
                  {t("addressBook.form.address")}
                </h3>
                <HeroInputField
                  control={form.control}
                  name="addressLine1"
                  label={t("addressBook.form.addressLine1")}
                  placeholder={t("addressBook.form.addressLine1Placeholder")}
                  variant="bordered"
                  labelPlacement="outside"
                />
                <HeroInputField
                  control={form.control}
                  name="addressLine2"
                  label={t("addressBook.form.addressLine2")}
                  placeholder={t("addressBook.form.addressLine2Placeholder")}
                  variant="bordered"
                  labelPlacement="outside"
                />
                <div className="grid grid-cols-2 gap-4">
                  <HeroInputField
                    control={form.control}
                    name="addressCity"
                    label={t("addressBook.form.city")}
                    placeholder={t("addressBook.form.cityPlaceholder")}
                    variant="bordered"
                    labelPlacement="outside"
                  />
                  <HeroInputField
                    control={form.control}
                    name="addressProvince"
                    label={t("addressBook.form.province")}
                    placeholder={t("addressBook.form.provincePlaceholder")}
                    variant="bordered"
                    labelPlacement="outside"
                  />
                  <HeroInputField
                    control={form.control}
                    name="addressPostalCode"
                    label={t("addressBook.form.postalCode")}
                    placeholder={t("addressBook.form.postalCodePlaceholder")}
                    variant="bordered"
                    labelPlacement="outside"
                  />
                  <HeroFormField
                    control={form.control}
                    name="addressCountry"
                    render={({ field, fieldState }) => (
                      <div className="space-y-2">
                        <label
                          className="text-sm font-medium text-default-700"
                          htmlFor={field.name}
                        >
                          {t("addressBook.form.country")}
                        </label>
                        <UiSelect
                          value={(field.value as string | undefined) ?? ""}
                          onValueChange={field.onChange}
                        >
                          <UiSelectTrigger
                            id={field.name}
                            aria-invalid={!!fieldState.error}
                            className={
                              fieldState.error ? "border-danger" : undefined
                            }
                          >
                            <UiSelectValue
                              placeholder={t(
                                "addressBook.form.countryPlaceholder"
                              )}
                            />
                          </UiSelectTrigger>
                          <UiSelectContent>
                            {countryOptions.map((country) => (
                              <UiSelectItem
                                key={country.code}
                                value={country.code}
                              >
                                {country.label}
                              </UiSelectItem>
                            ))}
                          </UiSelectContent>
                        </UiSelect>
                        {fieldState.error?.message && (
                          <p className="text-xs text-danger">
                            {fieldState.error.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold">
                  {t("addressBook.form.additionalInfo")}{" "}
                  <span className="text-default-500 text-xs font-normal">
                    {t("addressBook.form.allOptional")}
                  </span>
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <HeroSelectField
                    control={form.control}
                    name="label"
                    label={t("addressBook.form.label")}
                    placeholder={t("addressBook.form.labelPlaceholder")}
                    variant="bordered"
                    labelPlacement="outside"
                  >
                    <SelectItem key="Home">
                      {t("addressBook.form.home")}
                    </SelectItem>
                    <SelectItem key="Office">
                      {t("addressBook.form.office")}
                    </SelectItem>
                    <SelectItem key="Warehouse">
                      {t("addressBook.form.warehouse")}
                    </SelectItem>
                    <SelectItem key="Other">
                      {t("addressBook.form.other")}
                    </SelectItem>
                  </HeroSelectField>
                  <HeroSelectField
                    control={form.control}
                    name="propertyType"
                    label={t("addressBook.form.propertyType")}
                    placeholder={t("addressBook.form.propertyTypePlaceholder")}
                    variant="bordered"
                    labelPlacement="outside"
                  >
                    <SelectItem key={PropertyType.HOUSE}>
                      {t("addressBook.form.house")}
                    </SelectItem>
                    <SelectItem key={PropertyType.CONDO_LOBBY}>
                      {t("addressBook.form.condoLobby")}
                    </SelectItem>
                    <SelectItem key={PropertyType.CONDO_UNIT}>
                      {t("addressBook.form.condoUnit")}
                    </SelectItem>
                    <SelectItem key={PropertyType.OFFICE}>
                      {t("addressBook.form.office")}
                    </SelectItem>
                    <SelectItem key={PropertyType.WAREHOUSE}>
                      {t("addressBook.form.warehouse")}
                    </SelectItem>
                    <SelectItem key={PropertyType.RETAIL}>
                      {t("addressBook.form.retail")}
                    </SelectItem>
                  </HeroSelectField>
                </div>
                <HeroTextareaField
                  control={form.control}
                  name="pickupInstructions"
                  label={t("addressBook.form.pickupInstructions")}
                  placeholder={t(
                    "addressBook.form.pickupInstructionsPlaceholder"
                  )}
                  minRows={3}
                  variant="bordered"
                  labelPlacement="outside"
                />
                <HeroTextareaField
                  control={form.control}
                  name="deliveryInstructions"
                  label={t("addressBook.form.deliveryInstructions")}
                  description={t("addressBook.form.deliveryInstructionsHelp")}
                  placeholder={t(
                    "addressBook.form.deliveryInstructionsPlaceholder"
                  )}
                  minRows={3}
                  variant="bordered"
                  labelPlacement="outside"
                />
                <div className="flex items-center gap-6 flex-wrap">
                  <HeroSwitchField control={form.control} name="isDefault">
                    {t("addressBook.form.setAsDefault")}
                  </HeroSwitchField>
                  <HeroSwitchField control={form.control} name="isFavorite">
                    {t("addressBook.form.addToFavorites")}
                  </HeroSwitchField>
                  <HeroSwitchField
                    control={form.control}
                    name="propertyResidential"
                  >
                    {t("addressBook.form.residential")}
                  </HeroSwitchField>
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="light"
                type="button"
                onPress={onClose}
                isDisabled={isBusy}
              >
                {t("common:cancel")}
              </Button>
              <Button
                type="submit"
                color="primary"
                isDisabled={isBusy}
                isLoading={isBusy}
              >
                {isGeocoding
                  ? t("addressBook.form.verifying")
                  : isLoading
                    ? t("addressBook.form.saving")
                    : initialData
                      ? t("addressBook.form.updateContact")
                      : t("addressBook.form.addContact")}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={showUnverifiedConfirm}
        onOpenChange={setShowUnverifiedConfirm}
        placement="center"
        backdrop="blur"
        disableAnimation
        size="md"
      >
        <ModalContent>
          {(close) => (
            <>
              <ModalHeader>{t("addressBook.form.unverifiedTitle")}</ModalHeader>
              <ModalBody>
                <p className="text-sm text-default-600">
                  {confirmMessage} {t("addressBook.form.unverifiedSuffix")}
                </p>
              </ModalBody>
              <ModalFooter>
                <Button
                  color="primary"
                  onPress={() => {
                    setPendingValues(null);
                    close();
                  }}
                >
                  {t("addressBook.form.editAddress")}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      <Modal
        isOpen={showSuggestionDialog}
        onOpenChange={setShowSuggestionDialog}
        placement="center"
        backdrop="blur"
        disableAnimation
        size="lg"
      >
        <ModalContent>
          <ModalHeader className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" />
            {t("addressBook.form.suggestionTitle")}
          </ModalHeader>
          <ModalBody>
            <p className="text-sm">{t("addressBook.form.suggestionBody")}</p>

            {pendingValues && suggestedAddress && (
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="p-3 border border-default-200 rounded-lg bg-default-50">
                  <p className="text-xs font-medium text-default-500 mb-2">
                    {t("addressBook.form.yourEntry")}
                  </p>
                  <p className="text-sm text-foreground">
                    {pendingValues.addressLine1}
                  </p>
                  {pendingValues.addressLine2 && (
                    <p className="text-sm text-foreground">
                      {pendingValues.addressLine2}
                    </p>
                  )}
                  <p className="text-sm text-foreground">
                    {pendingValues.addressCity}, {pendingValues.addressProvince}{" "}
                    {pendingValues.addressPostalCode}
                  </p>
                  <p className="text-sm text-foreground">
                    {pendingValues.addressCountry}
                  </p>
                </div>

                <div className="p-3 border-2 border-primary/20 rounded-lg bg-primary/5">
                  <p className="text-xs font-medium text-primary mb-2">
                    {t("addressBook.form.googleSuggestion")}
                  </p>
                  <p className="text-sm text-foreground font-medium">
                    {suggestedAddress.addressLine1}
                  </p>
                  {suggestedAddress.addressLine2 && (
                    <p className="text-sm text-foreground">
                      {suggestedAddress.addressLine2}
                    </p>
                  )}
                  <p className="text-sm text-foreground">
                    {suggestedAddress.city}, {suggestedAddress.province}{" "}
                    {suggestedAddress.postalCode}
                  </p>
                  <p className="text-sm text-foreground">
                    {suggestedAddress.country}
                  </p>
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="light"
              onPress={() => {
                setShowSuggestionDialog(false);
                setSuggestedAddress(null);
                setVerifiedLocation(null);
                setPendingValues(null);
              }}
            >
              {t("common:cancel")}
            </Button>
            <Button variant="bordered" onPress={handleKeepOriginalAddress}>
              {t("addressBook.form.keepEntry")}
            </Button>
            <Button color="primary" onPress={handleUseSuggestedAddress}>
              {t("addressBook.form.useSuggestion")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
